import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input, type InputProps } from './Input';

export interface SearchInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'leftIcon' | 'rightSlot' | 'type'
> {
  /** Current committed (debounced) value, e.g. from URL/state. Used to reset the box. */
  value?: string;
  /** Called after the user stops typing for `delay` ms, on Enter (immediately) and on clear. */
  onSearch: (value: string) => void;
  delay?: number;
  /** Accessible label (no visible label is rendered). */
  'aria-label'?: string;
}

/**
 * Debounced search box. Typing is local; `onSearch` fires after `delay` ms (default 300) so the
 * API is not hit per keystroke. Enter fires immediately, Escape clears.
 */
export function SearchInput({
  value = '',
  onSearch,
  delay = 300,
  placeholder = 'Search…',
  'aria-label': ariaLabel = 'Search',
  ...rest
}: SearchInputProps) {
  const [text, setText] = useState(value);
  const last = useRef(value);
  const cb = useRef(onSearch);
  useEffect(() => {
    cb.current = onSearch;
  });

  // External reset (e.g. "clear filters" button): adjust state while rendering.
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    setText(value);
  }
  useEffect(() => {
    last.current = value;
  }, [value]);

  useEffect(() => {
    if (text === last.current) return;
    const t = setTimeout(() => {
      last.current = text;
      cb.current(text.trim());
    }, delay);
    return () => clearTimeout(t);
  }, [text, delay]);

  const commit = (v: string) => {
    last.current = v;
    cb.current(v.trim());
  };

  return (
    <Input
      type="search"
      role="searchbox"
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      leftIcon={<Search />}
      rightSlot={
        text ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setText('');
              commit('');
            }}
            className="rounded p-1.5 text-fg-subtle hover:text-fg"
          >
            <X className="size-4" />
          </button>
        ) : undefined
      }
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit(text);
        } else if (e.key === 'Escape' && text) {
          e.preventDefault();
          setText('');
          commit('');
        }
      }}
      {...rest}
    />
  );
}
